/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, { useState, useEffect } from "react"
import { render } from "react-dom"
import PropTypes from "prop-types"
import { useIntl } from "gatsby-plugin-intl"
import {
  GoogleMap,
  useLoadScript,
  MarkerClusterer,
} from "@react-google-maps/api"

import activePolylineSubject from "utils/activePolylineSubject"
import bouncingSubject from "utils/bouncingSubject"
import createBoundsFromPoints from "utils/createBoundsFromPoints"
import getBounds from "utils/getBounds"
import useWindowSize from "utils/useWindowSize"
import theme from "styles/theme"
import Spinner from "components/atoms/Spinner"
import Icon from "components/atoms/Icon"
import Spotlight from "components/molecules/Spotlight"
import ResetButton from "./ResetButton"
import MapLocation from "./MapLocation"
import ClusterPin from "./mapAssets/cluster.png"
import {
  StyledMapWrapper,
  StyledInformation,
  StyledOverviewInfo,
} from "./Map.styles"

const mapId = "f5ee4f091805c5fc"

const containerStyle = {
  width: "100%",
  height: "100%",
}

const initialCenter = {
  lat: 37.701477,
  lng: -98.136631,
}

const subTypePolygonColor = {
  ONE_HOUR_DRIVE: theme.color.septenary03,
  SUBMARKET: theme.color.quaternary03,
  MARKET: theme.color.senary03,
  "10": theme.color.quaternary03,
  "30": theme.color.senary03,
  "60": theme.color.septenary03,
}

const mapOptions = {
  streetViewControl: false,
  fullscreenControl: true,
  mapTypeControl: true,
  mapId,
  fullscreenControlOptions: {
    position: 7, // It is a value for RIGHT_TOP
  },
  mapTypeControlOptions: {
    position: 3, // It is a value for TOP_RIGHT
  },
  zoomControlOptions: {
    position: 3, // It is a value for RIGHT_TOP
  },
}

const clusterStyles = [
  {
    width: 28,
    height: 28,
    url: ClusterPin,
    textColor: "white",
    textSize: 10,
    className: "customCluster",
  },
]

const libraries = ["places", "directions"]

const Map = ({
  projectName,
  projectAddress,
  projects,
  onSelect,
  selectedId,
  mapType,
  projectPosition,
  mapContainerRef,
  isBannerActive,
}) => {
  const size = useWindowSize()
  const isDesktop = size.width >= theme.breakpoints.medium
  const [initialZoom] = useState(isDesktop ? 4 : 3)
  const [showSpotlight, setShowSpotlight] = useState(false)
  const [markerLocation, setMarkerLocation] = useState(null)
  const { formatMessage } = useIntl()
  const [map, setMap] = useState(null)
  const [mapContent, setMapContent] = useState(null)
  const [mapTypeId, setMapTypeId] = useState("roadmap")

  const [resetOptions, setResetOptions] = useState({
    zoom: initialZoom,
    center: initialCenter,
    bounds: null,
  })
  const [isMarkerBouncing, setIsMarkerBouncing] = useState(false)

  const defaultPadding = isDesktop
    ? {
        left: 500,
        right: 40,
        top: 40,
        bottom: 40,
      }
    : 0

  const zoomToHome = (value) => {
    setIsMarkerBouncing(value)
    const { coordinates } = projectPosition
    const center = {
      lat: coordinates[1],
      lng: coordinates[0] - 0.15,
    }

    if (value) {
      map.setZoom(11)
      map.setCenter(center)
    } else {
      // eslint-disable-next-line no-use-before-define
      setMapToDefault()
    }
  }

  const handleHideSpotlight = () => {
    setShowSpotlight(false)
  }

  useEffect(() => {
    if (map && mapType !== "portfolio") {
      bouncingSubject.attach(zoomToHome)
    }

    return bouncingSubject.detach(zoomToHome)
  }, [map, resetOptions])

  useEffect(() => {
    if (map && projects) {
      // eslint-disable-next-line no-use-before-define
      setMapContent(mapTypes[mapType]())
    }
  }, [projects, map, selectedId])

  const setMapToDefault = () => {
    const { zoom, center, bounds, padding } = resetOptions

    if (!isMarkerBouncing) {
      if (bounds) {
        map.fitBounds(bounds, padding)
      } else {
        map.setZoom(zoom)
        map.setCenter(center)
      }

      onSelect(null)
    }
  }

  useEffect(() => {
    if (map) {
      const controlButtonDiv = document.createElement("div")
      const mapControls = map.controls[7]

      if (mapControls.length) {
        mapControls.removeAt(0)
      }

      mapControls.push(controlButtonDiv)
      render(<ResetButton onClickHandler={setMapToDefault} />, controlButtonDiv)
      setMapToDefault()
    }
  }, [resetOptions, isMarkerBouncing])

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GATSBY_GOOGLE_API_KEY,
    libraries,
    mapIds: [mapId],
  })

  const onMapLoad = (googleMap) => {
    if (!map) setMap(googleMap)
  }

  const getLocation = (ref) => {
    setMarkerLocation(ref)
  }

  const createLocation = (locationOptions) => {
    const overlay = new window.google.maps.OverlayView()
    // eslint-disable-next-line func-names
    overlay.draw = function () {}
    overlay.setMap(map)

    return (
      <MapLocation
        key={locationOptions.project.id}
        isSelected={selectedId === locationOptions.project.id}
        locationOptions={{ overlay, ...locationOptions }}
        getMarkerLocation={getLocation}
        setShowSpotlight={setShowSpotlight}
      />
    )
  }

  const onDirectionsLoad = (directions) => {
    const { bounds } = directions.getDirections().routes[0]

    setResetOptions({
      zoom: initialZoom,
      center: initialCenter,
      bounds,
      padding: defaultPadding,
    })
  }

  const onPolygonLoadHandler = (polygon, padding = defaultPadding) => {
    const bounds = getBounds(polygon)
    setResetOptions({
      zoom: initialZoom,
      center: initialCenter,
      bounds,
      padding,
    })
  }

  const mapTypes = {
    portfolio: () => {
      const clusterOptions = {
        minimumClusterSize: 2,
        scaledSize: new window.google.maps.Size(20, 20),
      }
      return (
        <MarkerClusterer options={clusterOptions} styles={clusterStyles}>
          {(clusterer) =>
            projects.map((project) => createLocation({ project, clusterer }))
          }
        </MarkerClusterer>
      )
    },
    overview: () => {
      return (
        <>
          {projects.map((project, index) =>
            createLocation({
              pointType: project.subType,
              project: { id: index, ...project },
              onPolygonLoadHandler,
            })
          )}
        </>
      )
    },
    accessibility: () => {
      const polygonIndex = projects.findIndex(
        (project) => project.type === "Polygon"
      )
      if (polygonIndex >= 0 && isDesktop) {
        const polygon = projects.splice(polygonIndex, 1)
        const coordinatesList = polygon[0].coordinates[0].map(
          (coordinates) => ({
            lat: coordinates[1],
            lng: coordinates[0],
          })
        )

        const bounds = createBoundsFromPoints(coordinatesList)
        if (resetOptions.bounds.toString() !== bounds.toString()) {
          setResetOptions({
            zoom: initialZoom,
            center: initialCenter,
            bounds,
            padding: 0,
          })
        }
      } else {
        const filteredLocations = projects
          .filter(
            (project) =>
              project.type !== "Directions" &&
              project.type !== "PolygonInfoLabel"
          )
          .flatMap((project) => {
            if (project.type === "LineString") {
              return project.coordinates.map((coordinates) => ({
                lat: coordinates[1],
                lng: coordinates[0],
              }))
            }
            return {
              lat: project.coordinates[1],
              lng: project.coordinates[0],
            }
          })

        const bounds = createBoundsFromPoints(filteredLocations)

        setResetOptions({
          zoom: initialZoom,
          center: initialCenter,
          bounds,
          padding: {
            left: 5,
            right: 5,
            top: 100,
            bottom: 450,
          },
        })
      }

      return (
        <>
          {projects.map((project, index) =>
            createLocation({
              pointType: project.subType,
              polylinetype: project.subType,
              name: project.name,
              project: {
                id: index + project.type + project.subType,
                ...project,
              },
              onClickHandler: (_, firstPointCoordinates) => {
                if (
                  project.subType === "customAccessibility" ||
                  project.point
                ) {
                  const pointCoordinates = firstPointCoordinates || {
                    lng: project.point.coordinates[0],
                    lat: project.point.coordinates[1],
                  }
                  const [lng, lat] = projectPosition.coordinates
                  const bounds = createBoundsFromPoints([
                    pointCoordinates,
                    { lat, lng },
                  ])
                  map.fitBounds(bounds, defaultPadding)

                  project.clickHandler(pointCoordinates)
                }
              },
            })
          )}
        </>
      )
    },
    highlights: () => {
      const coordinatesList = projects
        .filter((project) => project.type === "Point")
        .map((project) => ({
          lat: project.coordinates[1],
          lng: project.coordinates[0],
        }))

      const bounds = createBoundsFromPoints(coordinatesList)

      if (resetOptions.bounds.toString() !== bounds.toString()) {
        setResetOptions({
          zoom: initialZoom,
          center: initialCenter,
          bounds,
          padding: isDesktop
            ? {
                left: 500,
                right: 40,
                top: 40,
                bottom: 40,
              }
            : {
                left: 0,
                right: 0,
                top: 20,
                bottom: 200,
              },
        })
      }
      const route = projects.find((project) => project.type === "Directions")
      if (route) {
        const { origin, destination } = route.coordinates.request
        const routeBounds = createBoundsFromPoints([
          origin.location,
          destination.location,
        ])
        map.fitBounds(routeBounds, { ...defaultPadding, bottom: 150 })
      }
      return (
        <>
          {projects.map((project, index) =>
            createLocation({
              project: {
                id: index + project.name + project.subType,
                ...project,
              },
              polylinetype: project.subType,
              pointType: project.subType,
              onClickHandler: project.clickHandler,
            })
          )}
        </>
      )
    },
    statistics: () => {
      return (
        <>
          {projects.map((project, index) => {
            return createLocation({
              project: { id: index + project.subType, ...project },
              onPolygonLoadHandler,
              pointType: project.subType,
              polylinetype: project.subType,
              options: {
                strokeColor: subTypePolygonColor[project.subType],
                fillColor: subTypePolygonColor[project.subType],
              },
            })
          })}
        </>
      )
    },
    traffic: () => {
      let firstPolygon = null
      const padding = isDesktop
        ? {
            left: 350,
            right: 0,
            top: 0,
            bottom: 0,
          }
        : {
            left: 0,
            right: 0,
            top: 20,
            bottom: 200,
          }

      const handleFirstPolygonLoad = (polygon) => {
        if (!firstPolygon) {
          onPolygonLoadHandler(polygon, padding)
          firstPolygon = polygon
        }
      }

      return (
        <>
          {projects.map((project, index) => {
            const getNumber =
              project.subType && project.subType.replace(/[^0-9]/g, "")

            return createLocation({
              project: {
                id: project.subType + project.name + index,
                ...project,
              },
              onPolygonLoadHandler: handleFirstPolygonLoad,
              onDirectionsLoadHandler: onDirectionsLoad,
              pointType: project.subType,
              polylinetype: project.subType,
              options: {
                strokeColor: subTypePolygonColor[getNumber],
                fillColor: subTypePolygonColor[getNumber],
              },
            })
          })}
        </>
      )
    },
  }

  const getmapTypeIds = () => {
    if (mapType === "overview" || mapType === "portfolio")
      return { mapTypeIds: ["roadmap", "satellite"], style: 2 } // It is a value for DROPDOWN_MENU
    if (map.mapTypeId === "satellite") {
      map.setMapTypeId("roadmap")
    }
    return { mapTypeIds: [], style: 1 }
  }

  const handleMapTypeIdChange = () => {
    if (map && mapType === "overview" && map.mapTypeId !== mapTypeId) {
      setMapTypeId(map.mapTypeId)
    }
  }

  const handleMapClick = () =>
    activePolylineSubject.notify({ action: "click", value: null })

  const renderMap = () => {
    return (
      <StyledMapWrapper>
        <GoogleMap
          center={initialCenter}
          onClick={handleMapClick}
          mapContainerStyle={containerStyle}
          zoom={initialZoom}
          options={{
            ...mapOptions,
            mapTypeControlOptions: {
              ...mapOptions.mapTypeControlOptions,
              ...getmapTypeIds(),
            },
          }}
          onLoad={onMapLoad}
          id="map"
          onMapTypeIdChanged={handleMapTypeIdChange}
        >
          {mapContent}
        </GoogleMap>
        {mapType === "overview" && mapTypeId === "roadmap" && (
          <StyledInformation>
            <StyledOverviewInfo onClick={() => map.setMapTypeId("satellite")}>
              {isDesktop ? (
                <>
                  <span role="img" aria-label="point right emoji">
                    👉
                  </span>
                  {formatMessage({
                    id: "pages.portfolio.project.location.overview.infoDesktop",
                  })}
                  <span role="img" aria-label="point left emoji">
                    👈
                  </span>
                </>
              ) : (
                <>
                  <Icon icon="info" />
                  {formatMessage({
                    id: "pages.portfolio.project.location.overview.infoMobile",
                  })}
                </>
              )}
            </StyledOverviewInfo>
          </StyledInformation>
        )}
        {isDesktop && showSpotlight && (
          <>
            <Spotlight
              markerLocation={markerLocation}
              projectName={projectName}
              projectAddress={projectAddress}
              mapContainerRef={mapContainerRef}
              handleHideSpotlight={handleHideSpotlight}
              showSpotlight={showSpotlight}
              isBannerActive={isBannerActive}
            />
          </>
        )}
      </StyledMapWrapper>
    )
  }

  if (loadError) {
    return (
      <div>
        {formatMessage({
          id: "pages.portfolio.map.error",
        })}
      </div>
    )
  }
  return isLoaded ? renderMap() : <Spinner />
}

Map.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      subType: PropTypes.string,
      coordinates: PropTypes.oneOfType([
        PropTypes.arrayOf(
          PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
        ),
        PropTypes.arrayOf(PropTypes.number),
        PropTypes.any,
      ]),
    })
  ),
  onSelect: PropTypes.func,
  selectedId: PropTypes.string,
  mapType: PropTypes.string,
  projectPosition: PropTypes.shape({
    type: PropTypes.string,
    coordinates: PropTypes.arrayOf(PropTypes.number),
  }),
}

Map.defaultProps = {
  selectedId: null,
  onSelect: () => null,
  projects: [],
  mapType: "portfolio",
  projectPosition: {},
}
export default Map
